import { DARK_OVERLAY } from '@flashka/shared/ui/styles/elevation';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '@/lib/utils';

// Legacy Radix popover, kept alive ONLY for call sites held back from the DS
// Tooltip/Popover migration (commit 87e8a8a9) — mostly dropdown-shaped surfaces
// (menus, pickers) that the DS Dropdown migration will own, since a dropdown is
// not a popover. New code belongs on the DS Popover: `@flashka/shared/ui`.
// The consumers are whatever still imports this — the importer list is the
// dropdown migration's worklist, so read it from git rather than a comment:
//   git grep -l "components/ui/popover" -- flashka-web/src

type PopoverBaseProps = React.ComponentProps<typeof PopoverPrimitive.Root>;
function PopoverBase(props: PopoverBaseProps) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

type PopoverTriggerProps = React.ComponentProps<typeof PopoverPrimitive.Trigger>;
function PopoverTrigger(props: PopoverTriggerProps) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

type PopoverAnchorProps = React.ComponentProps<typeof PopoverPrimitive.Anchor>;
function PopoverAnchor(props: PopoverAnchorProps) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Content> & {
  /** Render a downward-pointing tip on the same side the popover sits on,
   *  matching the Tooltip primitive's arrow vocabulary. Off by default — the
   *  breakdown popover on the Claim screen opts in to mirror Figma. */
  showArrow?: boolean;
  /**
   * Drop this primitive's own surface (background, border, radius, padding,
   * shadow) and its animation, keeping only positioning. For popovers that
   * bring their own panel — the shared DS `DropdownMenu` — where the legacy
   * chrome would double up and the hardcoded `animate-in` keyframes would
   * out-specify (and so shadow) the DS `.flashka-popup-content` one.
   */
  bare?: boolean;
};
function PopoverContent({
  className,
  align = 'center',
  sideOffset = 8,
  collisionPadding = 12,
  children,
  showArrow = false,
  bare = false,
  ...props
}: PopoverContentProps) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          'z-[202] max-w-[calc(100vw-32px)] outline-none',
          !bare && [
            // DS overlay chrome: the `secondary-200` hairline every DS menu,
            // popover and dialog draws (it auto-inverts, so no `dark:` swap —
            // a `secondary-300` dark border reads as a bright outline) and the
            // DS 0/2/8 @ 10% drop shadow, same value in both modes.
            'rounded-2xl border border-secondary-200 bg-popover px-4 py-3 text-popover-foreground shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            'text-pretty break-words leading-none',
            DARK_OVERLAY,
          ],
          className,
        )}
        {...props}>
        {children}
        {showArrow && (
          <PopoverPrimitive.Arrow
            className="-mb-px fill-secondary-100 [&_polygon]:stroke-secondary-200 [&_polygon]:stroke-1"
            width={10}
            height={5}
          />
        )}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

export {
  PopoverAnchor,
  type PopoverAnchorProps,
  PopoverBase,
  type PopoverBaseProps,
  PopoverContent,
  type PopoverContentProps,
  PopoverTrigger,
  type PopoverTriggerProps,
};
